import React, {useEffect, useId, useRef, useState} from 'react';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../../../s';
import {checkout} from '../../../../../api';
import {currency, currency_noNegatives, Portal} from '../../../../../util';
import {ICheckoutMessageType, ICheckoutStep, ICheckoutType} from '../../../../i';
import SummaryCartItems from './ab-redesign-summary-cart-items';
import TrustList from '../../../ab-step/ab-redesign-trust-list';

declare var modalOpen: any;

interface ISummaryRedesignProps {
    isMobile: boolean;
    /** Stacked layout, steps after the cart: the full summary rendered a second time below the step cards, never collapsed. */
    detail?: boolean;
    showEstBox: boolean;
    freeShipping: boolean;
    superSaverDiscount: number;
    onCheckoutTypeChange: (e: ICheckoutType) => void;
    onTriggerFocus: (id: string) => void;
    reloadPayment: () => void;
}

const openEstimate = () => modalOpen('modal-shipping-est-postal');

const EstimateButton = ({text, label}: {text: string; label: string}) => (
    <button
        type="button"
        className="cs-r-small-btn"
        onClick={openEstimate}
        aria-haspopup="dialog"
        aria-label={label}
    >
        {text}
    </button>
);

/** The design's (i) after the Shipping and Tax labels; it opens the same postal-code estimate modal as the buttons. */
const InfoButton = ({label}: {label: string}) => (
    <button
        type="button"
        className="cs-r-info"
        onClick={openEstimate}
        aria-haspopup="dialog"
        aria-label={label}
    >
        <i
            className="far fa-circle-info"
            aria-hidden={true}
        />
    </button>
);

const Spinner = () => (
    <i
        className="fas fa-circle-notch fa-spin"
        aria-hidden={true}
    />
);

const PromoRow = observer(({reloadPayment}: {reloadPayment: () => void}) => {
    const applied = store.checkout.code_promo_applied;
    const busy = store.uiCheckout.isLoadingCart;
    // The displayed text is local: the server keeps a failed code in code_promo.value and every full-copy response
    // would otherwise re-fill the box (and re-enable Apply) after the shopper cleared it. The store is still written on
    // change and right before Apply (the request reads it); the text is seeded from the store only for an applied code
    // (also on mount, so a remount after a failed Apply starts empty) and re-synced when a code becomes applied.
    const [code, setCode] = useState(store.checkout.code_promo_applied ? store.checkout.code_promo.value : '');
    // The server re-applies the (empty) promo code after an add-to-cart and leaves 'Invalid promo code - can not be empty.'
    // in the messages before the shopper typed anything, so promo messages only show once a code was submitted here.
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (applied) {
            setCode(store.checkout.code_promo.value);
        }
    }, [applied]);

    const submit = async () => {
        if (code.trim().length === 0 || busy) {
            return;
        }
        setSubmitted(true);
        runInAction(() => {
            store.checkout.code_promo.value = code;
            store.uiCheckout.isLoadingCart = true;
        });
        await checkout('discount-apply');
        runInAction(() => {
            store.uiCheckout.isLoadingCart = false;
        });
        reloadPayment();
    };

    const remove = async () => {
        runInAction(() => {
            store.uiCheckout.isLoadingCart = true;
        });
        await checkout('discount-remove');
        runInAction(() => {
            store.uiCheckout.isLoadingCart = false;
        });
        // The server cleared the code; the box follows (empty unless something is still applied).
        setCode(store.checkout.code_promo_applied ? store.checkout.code_promo.value : '');
        setSubmitted(false);
        reloadPayment();
    };

    return (
        <div className="cs-r-promo">
            <div className="cs-r-promo-row">
                <input
                    name="discount-code"
                    type="text"
                    maxLength={50}
                    autoComplete="off"
                    placeholder="Promo code"
                    aria-label="Promo code"
                    value={code}
                    readOnly={applied}
                    onChange={e => {
                        const value = e.target.value;
                        setCode(value);
                        runInAction(() => {
                            store.checkout.code_promo.value = value;
                        });
                        if (value.trim().length === 0) {
                            setSubmitted(false);
                        }
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !applied) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                />
                {!applied && (
                    <button
                        type="button"
                        className="cs-r-small-btn cs-r-apply"
                        disabled={code.trim().length === 0 || busy}
                        onClick={submit}
                        aria-label="Apply promo code"
                    >
                        Apply {busy && <Spinner />}
                    </button>
                )}
                {applied && (
                    <button
                        type="button"
                        className="cs-r-small-btn cs-r-apply"
                        disabled={busy}
                        onClick={remove}
                        aria-label="Remove promo code"
                    >
                        Remove {busy && <Spinner />}
                    </button>
                )}
            </div>
            {applied && <div className="cs-r-promo-msg cs-r-promo-ok">Promo code applied!</div>}
            {!applied &&
                submitted &&
                store.checkout.messages
                    .filter(o => o.msgType === ICheckoutMessageType.promo)
                    .map((o, i) => (
                        <div
                            key={i}
                            className="cs-r-promo-msg cs-r-promo-error"
                            role="alert"
                        >
                            {o.msg}
                        </div>
                    ))}
        </div>
    );
});

const SummaryRedesign = observer(({isMobile, detail = false, showEstBox, freeShipping, superSaverDiscount, onCheckoutTypeChange, onTriggerFocus, reloadPayment}: ISummaryRedesignProps) => {
    const [open, setOpen] = useState(false);
    const [proceedInView, setProceedInView] = useState(false);
    const proceedRef = useRef<HTMLButtonElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    // The stacked layout can mount two summaries at once (collapsible on top, full below the steps), so the body id must be unique.
    const bodyId = useId();
    const pricing = store.checkout.cart.pricing;
    const isCartStep = store.checkout.cstep <= ICheckoutStep.cart;
    const loadingEstimate = store.uiCheckout.isLoadingEstimated;
    const isEstimated = pricing.isEstShipping && !showEstBox;
    // The postal code behind the estimate: the one the shopper typed into the estimate modal, else the one the server
    // took from the IP geolocation. Shown next to the amounts so an estimate for the wrong place is obvious.
    const ship = store.checkout.ship;
    const estPostal = (ship.addr.postalCode.value.trim() === '' ? ship.estimatedPostalCode || '' : ship.addr.postalCode.value).trim();
    const shipAmount = pricing.shipping + pricing.production;
    const shippingIsFree = freeShipping || shipAmount <= 0;
    const payImg = `https://${Portal?.CDNDomain}/images/cached/general/pp_logo_h_100x26`;

    // In the stacked layout the summary sits above the checkout steps, so it starts collapsed to keep them on screen.
    const collapsible = isMobile && !isCartStep && !detail;
    const showBody = !collapsible || open;
    // Stacked cart step: the design pins TOTAL + Proceed to the bottom of the viewport while the cart lines scroll.
    const showStickyBar = isMobile && isCartStep && !detail;
    const totalLabel = isEstimated ? 'Estimated total' : 'Total';

    const proceed = () => {
        onCheckoutTypeChange(ICheckoutType.normal);
        onTriggerFocus('#checkout-as-guest');
    };

    useEffect(() => {
        const target = proceedRef.current;
        // Without IntersectionObserver (SSR, tests, old browsers) the bar simply stays visible.
        if (!showStickyBar || !target || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        // The bar covers the bottom of the viewport, so the in-page button only counts as visible once it clears the bar.
        const barHeight = barRef.current ? barRef.current.offsetHeight : 0;
        const io = new IntersectionObserver(entries => setProceedInView(entries.some(o => o.isIntersecting)), {rootMargin: `0px 0px -${barHeight}px 0px`});
        io.observe(target);
        return () => io.disconnect();
    }, [showStickyBar]);

    return (
        <div className={`cs-redesign${isMobile ? ' cs-r-mobile' : ''}${collapsible ? ' cs-r-collapsible' : ''}${detail ? ' cs-r-detail' : ''}`}>
            {collapsible ? (
                <h2 className="cs-r-title cs-r-title-toggle">
                    <button
                        type="button"
                        className="cs-r-toggle"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        onClick={() => setOpen(!open)}
                    >
                        <span>Order summary</span>
                        <i
                            className={`fas fa-chevron-${open ? 'up' : 'down'}`}
                            aria-hidden={true}
                        />
                    </button>
                </h2>
            ) : (
                <h2 className="cs-r-title">Order summary</h2>
            )}

            <div
                id={bodyId}
                className="cs-r-body"
            >
                <div className="cs-r-row">
                    <span>Subtotal</span>
                    <strong aria-label="Subtotal">{currency(pricing.subtotal)}</strong>
                </div>

                {showBody && (
                    <>
                        {pricing.discount !== 0 && (
                            <div className="cs-r-row">
                                {pricing.discount < 0 && <span>Add ({store.checkout.code_promo_text})</span>}
                                {pricing.discount > 0 && <span>{store.checkout.code_promo_text.length > 0 ? `Discount (${store.checkout.code_promo_text})` : 'Discount'}</span>}
                                <strong aria-label="Discount">{pricing.discount < 0 ? currency(pricing.discount * -1) : `-${currency(pricing.discount)}`}</strong>
                            </div>
                        )}

                        {/* While the amounts are an estimate (cart step: the geolocated ZIP) the labels say so; the ZIP itself is set on the Shipping step. */}
                        <div className="cs-r-row">
                            <span className="cs-r-label">
                                {isEstimated ? 'Estimated Shipping' : 'Shipping'}
                                {pricing.isEstShipping && <InfoButton label="Shipping estimate details" />}
                            </span>
                            {showEstBox && (
                                <EstimateButton
                                    text="Estimate"
                                    label="Estimate shipping"
                                />
                            )}
                            {!showEstBox && (
                                <strong aria-label="Shipping Cost">
                                    {loadingEstimate && <Spinner />}
                                    {!loadingEstimate && (shippingIsFree ? 'FREE' : currency(shipAmount + (isEstimated ? 0 : superSaverDiscount)))}
                                </strong>
                            )}
                        </div>

                        <div className="cs-r-row">
                            <span className="cs-r-label">
                                {isEstimated ? 'Estimated Tax' : 'Tax'}
                                {pricing.isEstShipping && <InfoButton label="Tax estimate details" />}
                            </span>
                            {showEstBox && (
                                <EstimateButton
                                    text="Estimate"
                                    label="Estimate tax"
                                />
                            )}
                            {!showEstBox && (
                                <strong aria-label="Tax">
                                    {loadingEstimate && <Spinner />}
                                    {!loadingEstimate && currency(pricing.tax)}
                                </strong>
                            )}
                        </div>

                        {isEstimated && estPostal !== '' && (
                            <div className="cs-r-est-note">
                                <span>
                                    Estimated for ZIP <strong>{estPostal}</strong>
                                </span>
                                {!loadingEstimate && (
                                    <EstimateButton
                                        text="Change"
                                        label={`Change the ZIP code used for the shipping and tax estimate, currently ${estPostal}`}
                                    />
                                )}
                            </div>
                        )}

                        {pricing.gift !== 0 && (
                            <div className="cs-r-row">
                                <span>Gift card</span>
                                <strong aria-label="Applied Gift Card Credit">-{currency(pricing.gift)}</strong>
                            </div>
                        )}

                        <div className="cs-r-divider" />

                        <PromoRow reloadPayment={reloadPayment} />

                        <div className="cs-r-divider" />
                    </>
                )}

                <div className="cs-r-row cs-r-total">
                    <span>{totalLabel}</span>
                    <strong aria-label="Total Cost">
                        {loadingEstimate && <Spinner />}
                        {!loadingEstimate && currency_noNegatives(store.getTotal)}
                    </strong>
                </div>

                {isCartStep && (
                    <>
                        <div className="cs-r-buttons">
                            <button
                                ref={proceedRef}
                                type="button"
                                className="btn-checkout btn-checkout-yellow"
                                aria-label="Proceed to checkout"
                                onClick={proceed}
                            >
                                Proceed to checkout
                            </button>

                            <button
                                type="button"
                                className="btn-checkout btn-checkout-white cs-r-paypal"
                                aria-label="Checkout with PayPal"
                                onClick={() => onCheckoutTypeChange(ICheckoutType.paypal_express)}
                            >
                                <picture>
                                    <source
                                        srcSet={`${payImg}.webp`}
                                        type="image/webp"
                                    />
                                    <img
                                        src={`${payImg}.png`}
                                        width={100}
                                        height={26}
                                        alt="Checkout with PayPal"
                                    />
                                </picture>
                            </button>

                            {store.payment_allow_apple && (
                                <button
                                    type="button"
                                    className="btn-checkout btn-checkout-white"
                                    aria-label="Checkout with Apple Pay"
                                    onClick={() => onCheckoutTypeChange(ICheckoutType.normal)}
                                >
                                    <i
                                        className="fab fa-apple-pay"
                                        aria-hidden={true}
                                    />
                                </button>
                            )}

                            {store.payment_allow_google && (
                                <button
                                    type="button"
                                    className="btn-checkout btn-checkout-white"
                                    aria-label="Checkout with Google Pay"
                                    onClick={() => onCheckoutTypeChange(ICheckoutType.normal)}
                                >
                                    <picture>
                                        <source
                                            srcSet={`https://${Portal?.CDNDomain}/images/cached/general/gpay.webp`}
                                            type="image/webp"
                                        />
                                        <img
                                            style={{maxHeight: 20, width: 'auto'}}
                                            src={`https://${Portal?.CDNDomain}/images/cached/general/gpay.png`}
                                            width={50}
                                            height={26}
                                            alt=""
                                            aria-hidden={true}
                                        />
                                    </picture>
                                </button>
                            )}

                            {store.payment_allow_venmo && (
                                <button
                                    type="button"
                                    className="btn-checkout btn-checkout-white"
                                    aria-label="Checkout with Venmo"
                                    onClick={() => onCheckoutTypeChange(ICheckoutType.normal)}
                                >
                                    <picture>
                                        <source
                                            srcSet={`https://${Portal?.CDNDomain}/images/cached/general/venmo_payment.webp`}
                                            type="image/webp"
                                        />
                                        <img
                                            style={{maxHeight: 20, width: 'auto'}}
                                            src={`https://${Portal?.CDNDomain}/images/cached/general/venmo_payment.png`}
                                            width={131}
                                            height={26}
                                            alt=""
                                            aria-hidden={true}
                                        />
                                    </picture>
                                </button>
                            )}
                        </div>

                        <TrustList className="cs-r-trust" />
                    </>
                )}

                {showBody && !isCartStep && <SummaryCartItems idPrefix={detail ? 'detail' : 'top'} />}
            </div>

            {showStickyBar && (
                <div
                    ref={barRef}
                    className={`cs-r-sticky-bar${proceedInView ? ' cs-r-sticky-hidden' : ''}`}
                >
                    <div className="cs-r-sticky-total">
                        <span>{totalLabel}</span>
                        <strong>
                            {loadingEstimate && <Spinner />}
                            {!loadingEstimate && currency_noNegatives(store.getTotal)}
                        </strong>
                    </div>
                    <button
                        type="button"
                        className="btn-checkout btn-checkout-yellow"
                        aria-label="Proceed to checkout"
                        onClick={proceed}
                    >
                        Proceed to checkout
                    </button>
                </div>
            )}
        </div>
    );
});

export default SummaryRedesign;
